import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { acGetAllPlanGroups, acGetPlanGroup, acGetAllPlans } from '../../store/actions'
import { DataTable } from '../default'
import { CreatePlanGroupModal, UpdatePlanGroupModal, DeletePlanGroupModal } from '../modals'

/**
 * @description PlanGroupsTable function displays a table of plan groups.
 * The function fetches all plan groups and plans from the server and renders them in a table format.
 * It also provides functionality to create, update, and delete plan groups.
 *
 * @author Maria Eduarda Franke
 * @returns {JSX.Element} The rendered table component with create, update, and delete modals.
 */
const PlanGroupsTable = () => {

    const dispatch = useDispatch()

    const [openCreateModal, setOpenCreateModal] = useState(false)
    const [openUpdateModal, setOpenUpdateModal] = useState(false)
    const [openDeleteModal, setOpenDeleteModal] = useState(false)

    // Get All Plan Groups
    const allPlanGroups = useSelector(state => state.planGroups.allPlanGroups)
    const isLoadingAllPlanGroups = useSelector(state => state.planGroups.isLoadingAllPlanGroups)
    const hasErrorAllPlanGroups = useSelector(state => state.planGroups.hasErrorAllPlanGroups)
    // Create, Update and Delete Plan Group
    const selectedPlanGroup = useSelector(state => state.planGroups.selectedPlanGroup)
    const isCreatingPlanGroup = useSelector(state => state.planGroups.isCreatingPlanGroup)
    const isUpdatingPlanGroup = useSelector(state => state.planGroups.isUpdatingPlanGroup)
    const isDeletingPlanGroup = useSelector(state => state.planGroups.isDeletingPlanGroup)

    useEffect(() => {
        dispatch(acGetAllPlanGroups())
            .catch(error => console.error(error))
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        dispatch(acGetAllPlans())
            .catch(error => console.error(error))
        // eslint-disable-next-line
    }, [])

    const columns = [
        {
            accessorKey: 'plan_group_id',
            header: 'ID',
            enableEditing: false,
            size: 80
        },
        {
            accessorKey: 'name',
            header: 'Nome'
        },
        {
            accessorKey: 'description',
            header: 'Descrição'
        },
        {
            accessorKey: 'is_default_table',
            header: 'Padrão',
            size: 80,
            Cell: ({ cell }) => renderDefaultTableIcon(cell)
        }
    ]

    const renderDefaultTableIcon = (cell) => {
        return cell.getValue() ? <CheckIcon color='primary' /> : <CloseIcon color='primary' />
    }

    // Handle Create Action
    const onCreate = () => {
        setOpenCreateModal(!openCreateModal)
    }

    // Handle Update Action
    const onUpdate = (row) => {
        dispatch(acGetPlanGroup(row.original.plan_group_id))
            .then(() => {
                setOpenUpdateModal(!openUpdateModal)
            })
            .catch(error => console.error(error))
    }

    // Handle Delete Action
    const onDelete = (row) => {
        dispatch(acGetPlanGroup(row.original.plan_group_id))
            .then(() => {
                setOpenDeleteModal(!openDeleteModal)
            })
            .catch(error => console.error(error))
    }

    return (
        <>
            <CreatePlanGroupModal
                open={openCreateModal}
                onClose={() => setOpenCreateModal(!openCreateModal)}
            />
            <UpdatePlanGroupModal
                open={openUpdateModal}
                onClose={() => setOpenUpdateModal(!openUpdateModal)}
                selectedPlanGroup={selectedPlanGroup}
            />
            <DeletePlanGroupModal
                open={openDeleteModal}
                onClose={() => setOpenDeleteModal(!openDeleteModal)}
                selectedPlanGroup={selectedPlanGroup}
            />
            <DataTable
                resource='Grupo de Planos'
                gender='masc'
                tableType='complete'
                columns={columns}
                data={allPlanGroups}
                onCreate={onCreate}
                onUpdate={onUpdate}
                onDelete={onDelete}
                isLoading={isLoadingAllPlanGroups}
                isSaving={isCreatingPlanGroup || isUpdatingPlanGroup || isDeletingPlanGroup}
                showAlertBanner={hasErrorAllPlanGroups}
            />
        </>
    )

}

export default PlanGroupsTable